import { sendGTMEvent } from '@next/third-parties/google'

export enum GTM_EVENT_CATEGORY {
  DONATION = 'DONATION',
  PRODUCT = 'PRODUCT',
  NEWSLETTER = 'NEWSLETTER',
  VENDOR_CLICK = 'VENDOR_CLICK',
  DISTRIBUTION = 'DISTRIBUTION',
  QUERY_LOCATION = 'QUERY_LOCATION',
  CTA_CLICK = 'CTA_CLICK',
}

export enum GTM_EVENT_SECTION {
  DONA_CTA_INTERSTITIAL = 'DonaCTAInterstitial',
  DONA_CTA_HEADER = 'DonaCTAHeader',
  DONA_CTA_FOOTER = 'DonaCTAFooter',
}

type GTMEventBase = {
  category: GTM_EVENT_CATEGORY
  event: string
  eventName: string
}

export type GTMEventWithSection = GTMEventBase & {
  section: string
}

export type GTMEventWithItemData = GTMEventBase & {
  cta_clicked: string
}

export type GTMEventWithCTAname = GTMEventBase & {
  buttonName: string
}

export type GTMEventWithLocationData = GTMEventBase & {
  search_type: string
}

type GTMEventMap = {
  [GTM_EVENT_CATEGORY.DONATION]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.PRODUCT]: GTMEventWithItemData
  [GTM_EVENT_CATEGORY.NEWSLETTER]: GTMEventBase
  [GTM_EVENT_CATEGORY.QUERY_LOCATION]: GTMEventWithLocationData
  [GTM_EVENT_CATEGORY.VENDOR_CLICK]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.DISTRIBUTION]: GTMEventWithCTAname
  [GTM_EVENT_CATEGORY.CTA_CLICK]: GTMEventWithItemData
}

const GTM_CONFIG = {
  [GTM_EVENT_CATEGORY.DONATION]: {
    event: 'donaButton',
    eventName: `dona_ora`,
  },

  [GTM_EVENT_CATEGORY.PRODUCT]: {
    event: `product_view`,
    eventName: `product_view`,
  },

  [GTM_EVENT_CATEGORY.NEWSLETTER]: {
    event: 'newsLetter',
    eventName: `sign_up`,
  },

  [GTM_EVENT_CATEGORY.QUERY_LOCATION]: {
    event: 'ricercaPiazza',
    eventName: 'ricerca_piazza_azalea',
  },

  // TODO support multiple vendors
  [GTM_EVENT_CATEGORY.VENDOR_CLICK]: {
    event: 'amazonButton',
    eventName: 'goto_amazon',
  },

  [GTM_EVENT_CATEGORY.DISTRIBUTION]: {
    event: 'puntiDistribuzione',
    eventName: 'punto_distribuzione',
    buttonName: 'punti di distribuzione',
  },

  [GTM_EVENT_CATEGORY.CTA_CLICK]: {
    event: `ricercaPiazzaAction`,
    eventName: `ricerca_piazza_action`,
  },
} as const

const SECTION_REQUIRED_CATEGORIES = new Set([
  GTM_EVENT_CATEGORY.DONATION,
  GTM_EVENT_CATEGORY.NEWSLETTER,
  GTM_EVENT_CATEGORY.VENDOR_CLICK,
])

/**
 * Invia un evento GTM con la configurazione appropriata
 * @param category - Categoria dell'evento GTM
 * @param additionalData - Dati aggiuntivi specifici per la categoria (es. section)
 * @throws Error se la configurazione è invalida o mancano dati richiesti
 */
export function dispatchGTMEvent<T extends GTM_EVENT_CATEGORY>(
  category: T,
  additionalData?: Partial<Omit<GTMEventMap[T], keyof GTMEventBase>>
): void {
  try {
    const config = GTM_CONFIG?.[category]

    if (!config) {
      throw new Error(`Invalid GTM configuration for category: ${category}`)
    }

    const needsSection = SECTION_REQUIRED_CATEGORIES.has(category)
    if (needsSection && !('section' in (additionalData || {}))) {
      throw new Error(`Section is required for ${category} events`)
    }

    const eventData = {
      ...config,
      ...additionalData,
    } as GTMEventMap[T]
    console.info('📡[GTM EVENT]:', {
      category,
      eventData,
    })

    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM ERROR]:', error)
    throw error
  }
}

// Example usage:
/*
dispatchGTMEvent(GTM_EVENT_CATEGORY.DONATION, { section: 'header' })
dispatchGTMEvent(GTM_EVENT_CATEGORY.NEWSLETTER)
*/
